import React from "react";
import { API } from "../API";
import { trimPatientInitials } from "../clockInOutUtils";
import useCaregiver from "../hooks/useCaregiver";
import { useUploadVerificationSelfieMutation } from "../hooks/useUploadVerificationSelfieMutation";
import { isLoading } from "../Loading";
import { PatientTaskInstanceDocument, VisitInstanceDutySheet, VisitInstanceId } from "../messages";
import { AppState } from "../system/AppState";
import { flmap } from "../utils/fmap";
import { CicoModalEntity, extractVisitFromCicoModalEntity } from "../utils/visitUtils";
import useVbpNursingQuestions from "./useVbpNursingQuestions";
import useVisitInstanceClockInOut from "./useVisitInstanceClockInOut";
import {
  MedflytVisitLetsStartBottomSheetProps,
  useMedflytVisitLetsStartBottomSheet,
} from "./VisitLetsStartBottomSheet/MedflytVisitLetsStartBottomSheet";

interface Props {
  activeEntity: CicoModalEntity;
  api: API;
  appState: AppState;
  children: (
    props: Omit<MedflytVisitLetsStartBottomSheetProps, "contactSupport">
  ) => React.ReactNode;
  isOpen: boolean;
  initialDutySheetData: VisitInstanceDutySheet;
  onHideOrShowVisitBottomSheet: (isOpen: boolean) => void;
  onOpenChangesSavedBanner: () => void;
  onSubmitDutySheet: (visitInstanceId: VisitInstanceId) => void;
  onUpdateActiveVisit: (visitInstanceId: VisitInstanceId) => void;
}

const WithMedflytLetsStartBottomSheet = (props: Props) => {
  const caregiver = useCaregiver(props.appState);
  const handleUploadVerificationSelfie = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const activeVisit = extractVisitFromCicoModalEntity(props.activeEntity);

  const {
    canClockIn,
    canClockOut,
    promptClockInMapModal,
    promptClockOutMapModal,
    clockInOutModalsState,
  } = useVisitInstanceClockInOut({
    visitInstance: activeVisit,
    caregiverId: flmap(caregiver, (c) => c.id),
    isCaregiverOfMedflyt: !isLoading(caregiver) ? caregiver.isCaregiverOfMedFlyt : false,
    api: props.api,
    appState: props.appState,
    onRequireManualClock: () => null,
    onModalClose: () => {
      props.onUpdateActiveVisit(activeVisit.id);
    },
    onUploadVerificationSelfie: (visitInstanceId, imageBase64, clockType) =>
      handleUploadVerificationSelfie.mutateAsync({
        visitInstanceId,
        imageBase64,
        clockType,
      }),
  });

  const { bottomSheetProps } = useMedflytVisitLetsStartBottomSheet({
    promptClockInMapModal,
    promptClockOutMapModal,
    activeEntity: props.activeEntity,
    onOpenChangesSavedBanner: props.onOpenChangesSavedBanner,
    postPatientDocAnswer: (visitInstanceId, documentTypeId, answer) =>
      props.api.postPatientDocAnswer(visitInstanceId, documentTypeId, answer),
    postSubmitPatientDocument: (visitInstanceId, documentTypeId, req) =>
      props.api.postSubmitPatientDocument(visitInstanceId, documentTypeId, req),
    isOpen: props.isOpen,
    onHideOrShowVisitBottomSheet: props.onHideOrShowVisitBottomSheet,
    initialDutySheetData: props.initialDutySheetData,
    onSubmitDutySheet: props.onSubmitDutySheet,
  });

  const {
    vbpNursingQuestions,
    changeVbpNursingQuestion,
    answerVbpNursingQuestion,
    submitVbpNursingReport,
  } = useVbpNursingQuestions({ visitInstanceId: activeVisit.id, api: props.api });

  const documents =
    props.activeEntity.type === "task"
      ? props.activeEntity.task.taskDetails.documents.filter(
          (doc): doc is PatientTaskInstanceDocument.PatientTaskInstanceDocumentRegular =>
            doc.type === "PatientTaskInstanceDocumentRegular"
        )
      : null;

  return (
    <>
      {isLoading(caregiver) || isLoading(vbpNursingQuestions)
        ? null
        : props.children({
            ...bottomSheetProps,
            patientInitials: trimPatientInitials(activeVisit.publicVisitDetails.patientInitials),
            visitAddress: activeVisit.confidentialVisitDetails.patientAddress,
            clockInOutModalsProps: clockInOutModalsState,
            canClockIn: canClockIn(),
            canClockOut: canClockOut(),
            vbpItemWithAnswers: vbpNursingQuestions,
            taskDocumentsCompleted:
              documents === null ? undefined : documents.filter((doc) => doc.completed).length,
            taskDocumentsTotal: documents === null ? undefined : documents.length,
            onChangeVBPQuestion: changeVbpNursingQuestion,
            onSubmitVBPQuestions: submitVbpNursingReport,
            onAnswerVBPQuestion: answerVbpNursingQuestion,
          })}
    </>
  );
};

export default WithMedflytLetsStartBottomSheet;
